const hiddenClass = "selling_plan_theme_integration--hidden";

class SellingPlansWidget {
  constructor(sellingPlansWidgetContainer) {
    this.sellingPlansWidgetContainer = sellingPlansWidgetContainer;
    this.updatePurchasingFrequencyLabels();
    this.appendSellingPlanInputs();
    this.updateSellingPlanInputsValues();
    this.listenToVariantChange();
    this.listenToSellingPlanFormRadioButtonChange();
    this.initDefaultSelection();
  }

  get sectionId() {
    return this.sellingPlansWidgetContainer.getAttribute("data-section-id");
  }

  get shopifySection() {
    return document.querySelector(`#shopify-section-${this.sectionId}`);
  }

  /*
    We are careful to target the correct form, as there are instances when we encounter an installment form that we specifically aim to avoid interacting with.
  */
  get variantIdInput() {
    return (
      this.addToCartForms[1]?.querySelector(`input[name="id"]`) ||
      this.addToCartForms[1]?.querySelector(`select[name="id"]`) ||
      this.addToCartForms[0].querySelector(`input[name="id"]`) ||
      this.addToCartForms[0].querySelector(`select[name="id"]`)
    );
  }

  get visibleSellingPlanForm() {
    return this.shopifySection.querySelector(
      `section[data-variant-id^="${this.variantIdInput.value}"]`
    );
  }

  get isVariantAvailable() {
    return this.selectedPurchaseOption.getAttributeNames().includes("disabled");
  }

  get sellingPlanInput() {
    return this.shopifySection.querySelector(".selected-selling-plan-id");
  }

  get addToCartForms() {
    return this.shopifySection.querySelectorAll('[action*="/cart/add"]');
  }

  /**
   * Recall the user's preferences via session storage, preselecting their selling plan preference, if any.
   */
  initDefaultSelection() {
    const sellingPlanType = sessionStorage.getItem('sellingPlanType');

    if (sellingPlanType === 'selling_plan') {
      const purchaseOptionInput = this.sellingPlansWidgetContainer.querySelector('.purchase-option-parent[data-radio-type="subscription_purchase"]');
      const sellingPlanValue = sessionStorage.getItem('sellingPlanName');
      
      // Check for select dropdown
      const sellingPlanSelect = this.sellingPlansWidgetContainer.querySelector('select.product-purchasing-select');
      if (sellingPlanSelect && sellingPlanValue) {
        const option = sellingPlanSelect.querySelector(`option[data-selling-plan-name="${sellingPlanValue}"]`);
        if (option) {
          sellingPlanSelect.value = option.value;
        }
      } else if (sellingPlanValue) {
        // Fallback to radio buttons
        const sellingPlanInput = this.sellingPlansWidgetContainer.querySelector(`input[type="radio"][data-selling-plan-name="${sellingPlanValue}"]`);
        if (sellingPlanInput) {
          sellingPlanInput.checked = true;
        }
      }

      if (purchaseOptionInput) {
        // Let SellingPlansWidget automatically handle this UI event via click.
        purchaseOptionInput.dispatchEvent(new MouseEvent('click', { bubbles: true }));
      }
    }
  }

  /*
    To enable the addition of a selling plan to a cart, it's necessary to include an input with the name "selling_plan", which will carry the selling ID as its value. When a buyer clicks on 'add to cart', the appropriate selling plan ID is added to their cart.
  */
  appendSellingPlanInputs() {
    this.addToCartForms.forEach((addToCartForm) => {
      addToCartForm.appendChild(this.sellingPlanInput.cloneNode());
    });
  }

  showSellingPlanForm(sellingPlanFormForSelectedVariant) {
    sellingPlanFormForSelectedVariant?.classList?.remove(hiddenClass);
  }

  hideSellingPlanForms(sellingPlanFormsForUnselectedVariants) {
    sellingPlanFormsForUnselectedVariants.forEach((element) => {
      element.classList.add(hiddenClass);
    });
  }

  /*
    Each product variant comes with a selling plan selection box that the buyer can interact with.
    When a buyer chooses a different variant, we ensure that only the relevant selling plan selection box is displayed.
    This guarantees that only the selling plan associated with the selected variant is shown.
  */
  handleSellingPlanFormVisibility() {
    const sellingPlanFormForSelectedVariant = this.shopifySection.querySelector(
      `section[data-variant-id="${this.variantIdInput.value}"]`
    );
    const sellingPlanFormsForUnselectedVariants =
      this.shopifySection.querySelectorAll(
        `.selling_plan_theme_integration:not([data-variant-id="${this.variantIdInput.value}"])`
      );
    this.showSellingPlanForm(sellingPlanFormForSelectedVariant);
    this.hideSellingPlanForms(sellingPlanFormsForUnselectedVariants);
  }

  handleVariantChange() {
    this.handleSellingPlanFormVisibility();
    this.updateSellingPlanInputsValues();
    this.listenToSellingPlanFormRadioButtonChange();
  }

  /*
    The functions listenToVariantChange() and listenToAddToCartForms() are implemented to track when a product variant is altered or when the product form is updated.
    The identification of the variant is crucial as it dictates which selling plan box should be displayed.
  */
  listenToVariantChange() {
    this.listenToAddToCartForms();
    if (this.variantIdInput.tagName === "INPUT") {
      const variantIdObserver = new MutationObserver((mutationList) => {
        mutationList.forEach((mutationRecord) => {
          this.handleVariantChange(mutationRecord.target.value);
        });
      });

      variantIdObserver.observe(this.variantIdInput, {
        attributes: true,
      });
    }
  }

  listenToAddToCartForms() {
    this.addToCartForms.forEach((addToCartForm) => {
      addToCartForm.addEventListener("change", () => {
        this.handleVariantChange();
      });
    });
  }

  updatePurchasingFrequencyLabels() {
    // This function is no longer needed for dropdowns, but keeping it for backwards compatibility
    const labels = this.sellingPlansWidgetContainer.querySelectorAll(
      ".product-purchasing-allocations label span"
    );
    labels.forEach((label) => {
      const text = label.innerText;
      const match = text.match(/\d+/); // Extracts the first number found
      if (match) {
        label.innerText = match[0]; // Updates the label with just the number
      }
    });
  }

  get sellingPlanAllocationPrice() {
    return document.getElementById(
      `${this.selectedPurchaseOption.dataset.sellingPlanGroupId}_allocation_price`
    );
  }

  get selectedPurchaseOptionPrice() {
    return this.selectedPurchaseOption.dataset.variantPrice;
  }

  get selectedPurchaseOptionComparedAtPrice() {
    return this.selectedPurchaseOption.dataset.variantCompareAtPrice;
  }

  get sellingPlanInputs() {
    return this.shopifySection.querySelectorAll(".selected-selling-plan-id");
  }

  updateSellingPlanInputsValues(event) {
    this.sellingPlanInputs.forEach((sellingPlanInput) => {
      sellingPlanInput.value = this.sellingPlanInputValue || '';
    });

    if (event?.currentTarget.dataset.radioType) {
      // Remember the user's purchase type selection.
      sessionStorage.setItem('sellingPlanType', this.radioType || 'one_time_purchase');
    }

    if (event?.currentTarget.dataset.sellingPlanName) {
      // Remeber the user's frequency preference.
      sessionStorage.setItem('sellingPlanName', this.sellingPlanInputName);
    }
  }

  get sellingPlanInputValue() {
    return this.selectedPurchaseOption?.dataset.sellingPlanId ?? null;
  }

  get sellingPlanInputName() {
    return this.selectedPurchaseOption?.dataset.sellingPlanName ?? null;
  }

  get radioType() {
    // Check if one-time is selected
    const oneTimeRadio = this.visibleSellingPlanForm?.querySelector(
      'input.purchase-option-parent[data-radio-type="one_time_purchase"]'
    );
    if (oneTimeRadio && oneTimeRadio.checked) {
      return 'one_time_purchase';
    }
    
    // Check if subscription is selected
    const subscriptionRadio = this.visibleSellingPlanForm?.querySelector(
      'input.purchase-option-parent[data-radio-type="subscription_purchase"]'
    );
    if (subscriptionRadio && subscriptionRadio.checked) {
      return 'subscription_purchase';
    }
    
    return this.selectedPurchaseOption?.dataset.radioType ?? null;
  }

  get selectedPurchaseOption() {
    // First check which purchase type is selected (one-time vs subscription)
    const subscriptionRadio = this.visibleSellingPlanForm?.querySelector(
      'input.purchase-option-parent[data-radio-type="subscription_purchase"]'
    );
    const oneTimeRadio = this.visibleSellingPlanForm?.querySelector(
      'input.purchase-option-parent[data-radio-type="one_time_purchase"]'
    );
    
    // If one-time is checked, return null (no selling plan)
    if (oneTimeRadio && oneTimeRadio.checked) {
      return null;
    }
    
    // If subscription is checked, look for the select dropdown or radio buttons
    if (subscriptionRadio && subscriptionRadio.checked) {
      // Check if there's a select dropdown
      const selectElement = this.visibleSellingPlanForm?.querySelector(
        'select.product-purchasing-select'
      );
      
      if (selectElement) {
        const selectedOption = selectElement.options[selectElement.selectedIndex];
        // Create a proxy object that mimics the radio button behavior
        return selectedOption ? {
          dataset: {
            sellingPlanId: selectedOption.dataset.sellingPlanId,
            sellingPlanName: selectedOption.dataset.sellingPlanName,
            sellingPlanGroupId: selectedOption.dataset.sellingPlanGroupId,
            variantPrice: selectedOption.dataset.variantPrice,
            variantCompareAtPrice: selectedOption.dataset.variantCompareAtPrice,
            radioType: selectElement.dataset.radioType
          },
          getAttributeNames: () => selectElement.disabled ? ['disabled'] : []
        } : null;
      }
      
      // Fallback to radio button for backwards compatibility
      return this.visibleSellingPlanForm?.querySelector(
        'input[type="radio"]:checked:not([data-radio-type="subscription_purchase"])'
      );
    }
    
    return null;
  }

  set selectedPurchaseOption(selectedPurchaseOption) {
    this._selectedPurchaseOption = selectedPurchaseOption;
  }

  handleRadioButtonChange(event) {
    this.selectedPurchaseOption = event.target;
    this.updateSellingPlanInputsValues(event);
  }

  listenToSellingPlanFormRadioButtonChange() {
    const purchasingOptions = this.visibleSellingPlanForm?.querySelectorAll(
      ".product-purchasing-option"
    );
    
    // Listen to select dropdowns
    const selectDropdowns = this.visibleSellingPlanForm?.querySelectorAll(
      'select.product-purchasing-select'
    );
    selectDropdowns?.forEach((select) => {
      select.addEventListener("change", (event) => {
        this.handleRadioButtonChange(event);
      });
    });
    
    // Also listen to radio buttons for backwards compatibility
    const radioButtons = this.visibleSellingPlanForm?.querySelectorAll(
      'input[type="radio"]'
    );
    radioButtons?.forEach((radio) => {
      radio.addEventListener("change", (event) => {
        if (radio.classList.contains("purchase-option-parent")) {
          purchasingOptions.forEach((option) => {
            option.classList.remove("active");
          });
          const radioParent = radio.parentNode;
          radioParent.classList.add("active");
          const purchasingFrequency = radioParent.querySelector(
            ".product-purchasing-frequency"
          );
          if (purchasingFrequency) {
            const preselectedSellingPlan = sessionStorage.getItem('sellingPlanName');
            
            // Check for select dropdown first
            const selectElement = purchasingFrequency.querySelector('select.product-purchasing-select');
            if (selectElement) {
              const optionQuerySelector = preselectedSellingPlan
                ? `option[data-selling-plan-name="${preselectedSellingPlan}"]`
                : 'option[data-default-plan]';
              const defaultPlanOption = selectElement.querySelector(optionQuerySelector);
              
              if (defaultPlanOption) {
                selectElement.value = defaultPlanOption.value;
                // Trigger change event to update the selling plan
                selectElement.dispatchEvent(new Event('change', { bubbles: true }));
              }
            } else {
              // Fallback to radio buttons
              const radioQuerySelector = preselectedSellingPlan
                ? `input[type="radio"][data-selling-plan-name="${preselectedSellingPlan}"]`
                : 'input[type="radio"][data-default-plan]';
              const defaultPlanOption = purchasingFrequency.querySelector(radioQuerySelector);

              if (defaultPlanOption) {
                defaultPlanOption.checked = true;
              }
            }
          }
        }
        this.handleRadioButtonChange(event);
        
        // Dispatch custom event for bundle validation
        if (radio.classList.contains("purchase-option-parent")) {
          const customEvent = new CustomEvent('purchaseOptionChanged', {
            detail: {
              radioType: radio.dataset.radioType,
              isSubscription: radio.dataset.radioType === 'subscription_purchase'
            }
          });
          document.dispatchEvent(customEvent);
        }
      });
    });
  }
}

document
  .querySelectorAll(".selling_plan_app_container")
  .forEach((sellingPlansWidgetContainer) => {
    new SellingPlansWidget(sellingPlansWidgetContainer);
  });
